import type { WeaponDefinition, WeaponContext, WeaponResult } from './types';
import { Entity, generateEntityId } from '../entities/entity';
import { WA_PHYSICS, type Vector2 } from '../core/constants';
import { getGameRNG } from '../core/rng';
import { crawlAlongSurface, acquireSurface } from '../sim/surfaceCrawler';

/** Frog crawl speed (px/s) - a little slower than the hopping sheep. */
const FROG_CRAWL_SPEED = 55;
/** Frog body half-extents (matches the 16x12 walker footprint). */
const FROG_HALF_W = 8;
const FROG_HALF_H = 6;

export interface SheepOptions {
  /** 'sheep' hops randomly; 'frog' walks the terrain in a fixed direction. */
  kind?: 'sheep' | 'frog';
  /** Initial launch velocity (frog is thrown in an arc, then walks). */
  launchVx?: number;
  launchVy?: number;
  canJump?: boolean;
  /** Frog: never reverse at a wall it cannot climb (keeps a fixed heading). */
  lockDirection?: boolean;
  lifetime?: number;
  bounciness?: number;
  explosionRadius?: number;
  maxDamage?: number;
}

export class Sheep implements Entity {
  id: string;
  position: Vector2;
  velocity: Vector2 = { x: 0, y: 0 };
  active = true;

  kind: 'sheep' | 'frog';
  facing: 'left' | 'right';
  isGrounded = false;
  walkSpeed = 100;
  jumpVelocity = 350;
  bounciness = 0.6;

  explosionRadius = 75;
  maxDamage = 75;

  ownerTeamId: string;

  private canJump: boolean;
  private lockDirection: boolean;
  private jumpCooldown = 0;
  private randomJumpTimer = 0;
  private lifetime: number;

  // Frog surface-crawl state (kind === 'frog'): contours the whole terrain
  // perimeter, including vertical faces and undersides.
  private crawling = false;
  private crawlNx = 0;
  private crawlNy = -1;

  onExplode?: (x: number, y: number) => void;
  onBaa?: () => void;

  constructor(
    x: number,
    y: number,
    facing: 'left' | 'right',
    ownerTeamId: string,
    opts: SheepOptions = {},
  ) {
    this.id = generateEntityId('sheep');
    this.position = { x, y };
    this.facing = facing;
    this.ownerTeamId = ownerTeamId;
    this.kind = opts.kind ?? 'sheep';
    this.canJump = opts.canJump ?? this.kind !== 'frog';
    this.lockDirection = opts.lockDirection ?? this.kind === 'frog';
    this.lifetime = opts.lifetime ?? (this.kind === 'frog' ? 3 : 10);
    this.bounciness = opts.bounciness ?? (this.kind === 'frog' ? 0 : 0.6);
    if (opts.explosionRadius !== undefined) this.explosionRadius = opts.explosionRadius;
    if (opts.maxDamage !== undefined) this.maxDamage = opts.maxDamage;

    if (opts.launchVx !== undefined || opts.launchVy !== undefined) {
      // Thrown (frog): arc through the air, then walk once it lands.
      this.velocity.x = opts.launchVx ?? 0;
      this.velocity.y = opts.launchVy ?? 0;
      this.isGrounded = false;
    } else {
      this.velocity.x = facing === 'right' ? this.walkSpeed : -this.walkSpeed;
    }
    if (this.canJump) {
      this.randomJumpTimer = getGameRNG().nextFloat(0.3, 0.8);
    }
  }

  update(deltaTime: number, terrain?: { isSolid(x: number, y: number): boolean }): void {
    if (!this.active) return;

    if (this.kind === 'frog') {
      // The frog's fuse only starts counting once it has landed and begun
      // crawling - arcing through the air does not burn the timer.
      if (this.crawling) {
        this.lifetime -= deltaTime;
        if (this.lifetime <= 0) {
          this.explode();
          return;
        }
      }
      this.updateFrog(deltaTime, terrain);
      return;
    }

    this.lifetime -= deltaTime;
    if (this.lifetime <= 0) {
      this.explode();
      return;
    }

    if (!this.isGrounded) {
      this.velocity.y += WA_PHYSICS.GRAVITY * deltaTime;
    } else {
      this.velocity.x = this.facing === 'right' ? this.walkSpeed : -this.walkSpeed;

      if (this.canJump) {
        this.randomJumpTimer -= deltaTime;
        if (this.randomJumpTimer <= 0 && this.jumpCooldown <= 0) {
          this.velocity.y = -this.jumpVelocity * getGameRNG().nextFloat(0.5, 1.0);
          this.isGrounded = false;
          this.jumpCooldown = 0.2;
          this.randomJumpTimer = getGameRNG().nextFloat(0.5, 1.5);
          this.onBaa?.();
        }
      }
    }

    const width = 16;
    const height = 12;
    const maxClimb = 6;

    if (this.velocity.x !== 0 && terrain && this.isGrounded) {
      const moveX = this.velocity.x * deltaTime;
      const nextX = this.position.x + moveX;
      const edgeX = moveX > 0 ? nextX + width : nextX;

      let moved = false;
      for (let climbY = 0; climbY <= maxClimb; climbY++) {
        const testY = this.position.y - climbY;
        const blocked =
          terrain.isSolid(edgeX, testY + 2) || terrain.isSolid(edgeX, testY + height / 2);

        if (!blocked) {
          this.position.x = nextX;
          this.position.y = testY;

          for (let dropY = 0; dropY <= maxClimb + 2; dropY++) {
            const feetY = this.position.y + height;
            if (terrain.isSolid(this.position.x + width / 2, feetY + dropY)) {
              this.position.y = this.position.y + dropY;
              break;
            }
          }
          moved = true;
          break;
        }
      }

      if (!moved) {
        if (this.lockDirection) {
          // Frog keeps its heading; a wall it can't climb just halts it.
          this.velocity.x = 0;
        } else {
          this.facing = this.facing === 'right' ? 'left' : 'right';
          this.velocity.x = -this.velocity.x;
        }
      }
    } else if (this.velocity.x !== 0 && terrain && !this.isGrounded) {
      const moveX = this.velocity.x * deltaTime;
      this.position.x += moveX;
    }

    this.position.y += this.velocity.y * deltaTime;

    if (this.jumpCooldown > 0) {
      this.jumpCooldown -= deltaTime;
    }
  }

  /**
   * Frog motion: thrown in an arc, then crawls the terrain CONTOUR in its
   * fixed direction - up walls, across overhangs and along undersides - until
   * its fuse runs out. Owns its own positioning (applyTerrainPhysics is a
   * no-op for frogs) so the surface crawl is not fought by gravity snapping.
   */
  private updateFrog(
    deltaTime: number,
    terrain?: { isSolid(x: number, y: number): boolean },
  ): void {
    if (!terrain) {
      // No terrain context: simple ballistic fall.
      this.velocity.y += WA_PHYSICS.GRAVITY * deltaTime;
      this.position.x += this.velocity.x * deltaTime;
      this.position.y += this.velocity.y * deltaTime;
      return;
    }

    if (this.crawling) {
      const cx = this.position.x + FROG_HALF_W;
      const cy = this.position.y + FROG_HALF_H;
      const { state, lostContact } = crawlAlongSurface(
        terrain,
        { x: cx, y: cy, nx: this.crawlNx, ny: this.crawlNy },
        this.facing,
        FROG_CRAWL_SPEED * deltaTime,
      );
      if (lostContact) {
        // Walked off an isolated tip: drop back into a short fall, re-acquire.
        this.crawling = false;
        this.isGrounded = false;
        this.velocity.x = 0;
        this.velocity.y = 0;
        return;
      }
      this.position.x = state.x - FROG_HALF_W;
      this.position.y = state.y - FROG_HALF_H;
      this.crawlNx = state.nx;
      this.crawlNy = state.ny;
      this.isGrounded = true;
      return;
    }

    // Airborne: ballistic arc with sampled contact detection (no tunnelling).
    this.velocity.y += WA_PHYSICS.GRAVITY * deltaTime;
    const stepX = this.velocity.x * deltaTime;
    const stepY = this.velocity.y * deltaTime;
    const startCx = this.position.x + FROG_HALF_W;
    const startCy = this.position.y + FROG_HALF_H;
    const steps = Math.max(1, Math.ceil(Math.hypot(stepX, stepY) / 3));
    for (let s = 1; s <= steps; s++) {
      const cx = startCx + (stepX * s) / steps;
      const cy = startCy + (stepY * s) / steps;
      if (terrain.isSolid(Math.round(cx), Math.round(cy))) {
        this.beginCrawl(cx, cy, terrain);
        return;
      }
    }
    this.position.x += stepX;
    this.position.y += stepY;
  }

  /** Latch onto the surface at (cx, cy) and switch into crawl mode. */
  private beginCrawl(
    cx: number,
    cy: number,
    terrain: { isSolid(x: number, y: number): boolean },
  ): void {
    const st = acquireSurface(terrain, cx, cy, { x: this.crawlNx, y: this.crawlNy });
    if (!st) return;
    this.position.x = st.x - FROG_HALF_W;
    this.position.y = st.y - FROG_HALF_H;
    this.crawlNx = st.nx;
    this.crawlNy = st.ny;
    this.crawling = true;
    this.isGrounded = true;
    this.velocity.x = 0;
    this.velocity.y = 0;
  }

  applyTerrainPhysics(terrain: { isSolid(x: number, y: number): boolean }): void {
    if (!this.active) return;

    // Frog manages its own surface-crawl positioning in updateFrog.
    if (this.kind === 'frog') return;

    const width = 16;
    const height = 12;
    const feetY = this.position.y + height;

    if (this.isEmbeddedInTerrain(width, height, terrain)) {
      this.escapeFromTerrain(width, height, terrain);
    }

    if (this.velocity.y >= 0) {
      for (let dy = 0; dy <= 5; dy++) {
        if (terrain.isSolid(this.position.x + width / 2, feetY + dy)) {
          this.position.y = feetY + dy - height;

          if (this.velocity.y > 100) {
            this.velocity.y = -this.velocity.y * this.bounciness;
            this.onBaa?.();
          } else {
            this.velocity.y = 0;
            this.isGrounded = true;
          }
          return;
        }
      }
    }

    const hasGroundBelow =
      terrain.isSolid(this.position.x + 2, feetY + 1) ||
      terrain.isSolid(this.position.x + width / 2, feetY + 1) ||
      terrain.isSolid(this.position.x + width - 2, feetY + 1);
    if (!hasGroundBelow) {
      this.isGrounded = false;
    }
  }

  checkWaterCollision(waterLevel: number, worldHeight: number): boolean {
    const waterY = worldHeight - waterLevel;
    return this.position.y > waterY;
  }

  private isEmbeddedInTerrain(
    width: number,
    height: number,
    terrain: { isSolid(x: number, y: number): boolean },
  ): boolean {
    const checkPoints = [
      { x: this.position.x + 2, y: this.position.y + 2 },
      { x: this.position.x + width - 2, y: this.position.y + 2 },
      { x: this.position.x + 2, y: this.position.y + height / 2 },
      { x: this.position.x + width - 2, y: this.position.y + height / 2 },
      { x: this.position.x + width / 2, y: this.position.y + height / 2 },
    ];
    return checkPoints.some((p) => terrain.isSolid(p.x, p.y));
  }

  private escapeFromTerrain(
    width: number,
    height: number,
    terrain: { isSolid(x: number, y: number): boolean },
  ): void {
    const startX = this.position.x;
    const startY = this.position.y;

    for (let dist = 1; dist <= 40; dist++) {
      for (let angle = 0; angle < 360; angle += 45) {
        const rad = (angle * Math.PI) / 180;
        const testX = startX + Math.cos(rad) * dist;
        const testY = startY + Math.sin(rad) * dist;

        this.position.x = testX;
        this.position.y = testY;

        if (!this.isEmbeddedInTerrain(width, height, terrain)) {
          this.velocity.x = 0;
          this.velocity.y = 0;
          return;
        }
      }
    }

    this.position.x = startX;
    this.position.y = startY;
  }

  explode(): void {
    if (!this.active) return;
    this.active = false;
    this.onExplode?.(this.position.x + 8, this.position.y + 6);
  }

  destroy(): void {
    this.active = false;
  }

  detonate(): void {
    this.explode();
  }
}

export const WeaponSheep: WeaponDefinition = {
  id: 'sheep',
  name: 'Sheep',
  icon: 'sheep.1.png',
  category: 'thrown',
  maxAmmo: 2,
  maxUsesPerTurn: 1,
  powerCharging: false,
  timerAdjustable: false,
  showAimGuide: false,
  allowRetreat: true,

  fire(ctx: WeaponContext): WeaponResult {
    const { worm } = ctx;

    const spawnX = worm.facing === 'right' ? worm.position.x + 20 : worm.position.x - 20;
    const spawnY = worm.position.y + 4;

    const sheep = new Sheep(spawnX, spawnY, worm.facing, worm.teamId);

    return {
      success: true,
      entities: [sheep],
      message: 'Baa!',
    };
  },
};
